import React, { FC, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import {
  AllPlanAutoAcceptScheduleType,
  AllPlanScheduleType,
  SchedulerPreference,
} from '@demind-inc/core';
import { useTranslation } from 'react-i18next';

import { usePrepareScheduler } from '../../../../../data-access';
import { AppSwitch } from '../../../../common';
import './AIAssistantReplanSettings.scss';

const INCLUDE_TYPES: Array<{ key: AllPlanScheduleType; labelKey: string; icon: string }> = [
  { key: 'meal', labelKey: 'settings.aiAssistant.planTypes.meal', icon: '🍽️' },
  { key: 'workout', labelKey: 'settings.aiAssistant.planTypes.workout', icon: '💪' },
  { key: 'routine', labelKey: 'settings.aiAssistant.planTypes.routine', icon: '🔁' },
  { key: 'sleep', labelKey: 'settings.aiAssistant.planTypes.sleep', icon: '🌙' },
  { key: 'todo', labelKey: 'settings.aiAssistant.planTypes.tasks', icon: '✅' },
];

const AUTO_ACCEPT_TYPES: Array<{
  key: AllPlanAutoAcceptScheduleType;
  labelKey: string;
  icon: string;
}> = [
  { key: 'meal', labelKey: 'settings.aiAssistant.planTypes.meal', icon: '🍽️' },
  { key: 'workout', labelKey: 'settings.aiAssistant.planTypes.workout', icon: '💪' },
  { key: 'sleep', labelKey: 'settings.aiAssistant.planTypes.sleep', icon: '🌙' },
];

const AIAssistantReplanSettings: FC = () => {
  const { t } = useTranslation();
  const { schedulerPreferences, updatePreferences, isPreferenceLoading } = usePrepareScheduler();
  const [includeTypes, setIncludeTypes] = useState<Record<string, boolean | undefined>>({});
  const [autoAcceptTypes, setAutoAcceptTypes] = useState<Record<string, boolean | undefined>>({});
  const [skipAllPlanning, setSkipAllPlanning] = useState(false);
  const [skipReplanCompletedPastTask, setSkipReplanCompletedPastTask] = useState(false);
  const [skipReplanCompletedPastRoutine, setSkipReplanCompletedPastRoutine] = useState(false);

  useEffect(() => {
    if (!schedulerPreferences?.allPlan) {
      return;
    }
    setIncludeTypes({ ...(schedulerPreferences.allPlan.includeTypes ?? {}) });
    setAutoAcceptTypes({ ...(schedulerPreferences.allPlan.autoAcceptTypes ?? {}) });
    setSkipAllPlanning(
      !!(schedulerPreferences.allPlan as { skipAllPlanning?: boolean }).skipAllPlanning
    );

    const todoPref = schedulerPreferences.allPlan.todo;
    const routinePref = schedulerPreferences.allPlan.routine;
    setSkipReplanCompletedPastTask(!!todoPref?.skipReplanInCompletedPastTask);
    setSkipReplanCompletedPastRoutine(!!routinePref?.skipReplanInCompletedPastRoutine);
  }, [schedulerPreferences]);

  const updateAllPlanPreferences = (
    nextAllPlan: Partial<NonNullable<SchedulerPreference['allPlan']>>
  ) => {
    updatePreferences({
      allPlan: {
        ...schedulerPreferences?.allPlan,
        ...nextAllPlan,
      } as SchedulerPreference['allPlan'],
    }).catch(() => undefined);
  };

  const isDisabled = useMemo(
    () => isPreferenceLoading || skipAllPlanning,
    [isPreferenceLoading, skipAllPlanning]
  );

  const handleIncludeTypeToggle = (key: AllPlanScheduleType, value: boolean) => {
    const next = { ...includeTypes, [key]: value };
    const nextSkipAllPlanning = Object.values(next).every((isIncluded) => !isIncluded);
    setIncludeTypes(next);
    setSkipAllPlanning(nextSkipAllPlanning);
    updatePreferences({
      allPlan: {
        ...schedulerPreferences?.allPlan,
        includeTypes: next,
        skipAllPlanning: nextSkipAllPlanning,
      } as SchedulerPreference['allPlan'] & { skipAllPlanning?: boolean },
    }).catch(() => undefined);
  };

  const handleAutoAcceptTypeToggle = (key: AllPlanAutoAcceptScheduleType, value: boolean) => {
    const next = { ...autoAcceptTypes, [key]: value };
    setAutoAcceptTypes(next);
    updatePreferences({
      allPlan: {
        ...schedulerPreferences?.allPlan,
        autoAcceptTypes: next,
      },
    }).catch(() => undefined);
  };

  const handleSkipReplanCompletedPastTaskToggle = (value: boolean) => {
    setSkipReplanCompletedPastTask(!value);
    updateAllPlanPreferences({
      todo: {
        ...(schedulerPreferences?.allPlan?.todo ?? {}),
        skipReplanInCompletedPastTask: !value,
      },
    });
  };

  const handleSkipReplanCompletedPastRoutineToggle = (value: boolean) => {
    setSkipReplanCompletedPastRoutine(!value);
    updateAllPlanPreferences({
      routine: {
        ...(schedulerPreferences?.allPlan?.routine ?? {}),
        skipReplanInCompletedPastRoutine: !value,
      },
    });
  };

  if (isPreferenceLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" flex={1}>
        <CircularProgress size={16} />
      </Stack>
    );
  }

  return (
    <Stack gap={2} className="ai-assistant-replan-settings">
      <Stack gap={1}>
        <Typography className="ai-assistant-replan-settings__title" variant="subtitle2">
          {t('settings.aiAssistant.autoAcceptSettings')}
        </Typography>
        <Typography className="ai-assistant-replan-settings__hint" variant="caption">
          {t('settings.aiAssistant.autoAcceptHint')}
        </Typography>
        {AUTO_ACCEPT_TYPES.map((item) => (
          <div className="ai-assistant-replan-settings__row" key={item.key}>
            <Typography variant="body2">{`${item.icon} ${t(item.labelKey)}`}</Typography>
            <AppSwitch
              checked={!!autoAcceptTypes[item.key]}
              onChange={(_event, checked) => handleAutoAcceptTypeToggle(item.key, checked)}
              disabled={isDisabled}
            />
          </div>
        ))}
      </Stack>

      <Divider />

      <Stack gap={1}>
        <Typography className="ai-assistant-replan-settings__title" variant="subtitle2">
          {t('settings.aiAssistant.planTypes.tasks')}
        </Typography>
        <div className="ai-assistant-replan-settings__row">
          <Typography variant="body2">
            ✅ {t('settings.aiAssistant.replanUnfinishedTasks')}
          </Typography>
          <AppSwitch
            checked={!skipReplanCompletedPastTask}
            onChange={(_event, checked) => handleSkipReplanCompletedPastTaskToggle(checked)}
            disabled={isDisabled}
          />
        </div>
      </Stack>

      <Divider />

      <Stack gap={1}>
        <Typography className="ai-assistant-replan-settings__title" variant="subtitle2">
          {t('settings.aiAssistant.planTypes.routine')}
        </Typography>
        <div className="ai-assistant-replan-settings__row">
          <Typography variant="body2">
            ✅ {t('settings.aiAssistant.replanUnfinishedRoutines')}
          </Typography>
          <AppSwitch
            checked={!skipReplanCompletedPastRoutine}
            onChange={(_event, checked) => handleSkipReplanCompletedPastRoutineToggle(checked)}
            disabled={isDisabled}
          />
        </div>
      </Stack>
    </Stack>
  );
};

export default AIAssistantReplanSettings;
